import React from 'react';
import { Header } from '@/components/layout/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Building2, Settings, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PrivateRoute } from '@/components/PrivateRoute';
import { useUser } from '@clerk/clerk-react';
import { ComplianceSettings } from '@/components/settings/ComplianceSettings';
import { httpRequest } from '@/lib/http-client';
import { UsersManagement } from '@/domain/admin/components/UsersManagement';
import { TenantsManagement } from '@/domain/admin/components/TenantsManagement';

export default function Admin() {
  const { user } = useUser();

  const handleSyncOfac = async () => {
    try {
      await httpRequest('/api/admin/sync-ofac', {
        method: 'POST',
      });
      toast({
        title: 'OFAC data synced successfully',
      });
    } catch (error) {
      console.error('Failed to sync OFAC data:', error);
      toast({
        title: 'Failed to sync OFAC data',
        variant: 'destructive',
      });
    }
  };

  return (
    <PrivateRoute requiredRole="admin">
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
              <p className="text-muted-foreground">
                Signed in as {user?.primaryEmailAddress?.emailAddress}
              </p>
            </div>
          </div>

          <Tabs defaultValue="users" className="space-y-4">
            <TabsList>
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Users</span>
              </TabsTrigger>
              <TabsTrigger value="organization" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span>Companies</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="users" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Invite the user in Clerk first. After they accept and verify their email,
                assign their account to the correct company here.
              </p>
              <UsersManagement />
            </TabsContent>

            <TabsContent value="organization" className="space-y-4">
              <TenantsManagement />
            </TabsContent>

            <TabsContent value="settings" className="space-y-4">
              <div className="grid gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">System Settings</h2>
                    <p className="text-sm text-muted-foreground">
                      Configure system-wide settings and integrations
                    </p>
                  </div>
                </div>
                <div className="grid gap-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-medium">OFAC Data Sync</h3>
                      <p className="text-sm text-muted-foreground">
                        Manually trigger an OFAC data synchronization
                      </p>
                    </div>
                    <Button
                      onClick={handleSyncOfac}
                    >
                      <Shield className="mr-2 h-4 w-4" />
                      Sync Now
                    </Button>
                  </div>
                  
                  {/* Compliance Settings */}
                  <ComplianceSettings />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </PrivateRoute>
  );
}
